import {Add, DeleteRounded, DescriptionRounded, ExpandMore} from "@mui/icons-material";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Grid,
    IconButton,
    TextField,
    Typography
} from "@mui/material";
import * as React from "react";
import {useContext, useEffect, useRef, useState} from "react";
import {Controller, useFormContext} from "react-hook-form";
import {useTranslation} from "react-i18next";
import AgGridCustomTable from "../../../../../../util/component/AgGridCustomTable";
import {ColDef, GetRowIdParams, GridOptions, ICellRendererParams} from "ag-grid-enterprise";
import dayjs from "dayjs";
import {setSnackbar} from "../../../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../../../util/state/globalVariables";
import {useAgGridLocale, useHasScope} from "../../../../../../config/globalConfig";
import {AgGridReact} from "ag-grid-react";
import {useTheftMisappropriationActions} from "../../../../service/useTheftMisappropriationActions";
import {GlobalContext} from "../../../../../../util/state/GlobalContextProvider";
import {useParams} from "react-router-dom";
import {useRecoilValue} from "recoil";
import {EDITING} from "../../../../service/FinesSecurityRecoil";
import {DatePicker} from "@mui/x-date-pickers";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import CustomModalBL from "../../../../component/CustomModalBL";
import {
    THEFT_MISAPPROPRIATION_SCOPE_DETAIL,
    THEFT_MISAPPROPRIATION_SCOPE_EDIT
} from "../../../../component/utils/AuthConstants";

const ComplainantDetails = (props: any) => {
    const {t} = useTranslation(['finessecurity'], {keyPrefix: 'theftMisappropriate'});
    const hasScope = useHasScope;
    const {id} = useParams();
    const [isExpanded, setIsExpanded] = useState(true);
    const [fileForImport, setAttachment] = useState<File | undefined>(undefined);
    const agGridLocale = useAgGridLocale();
    const gridRef = useRef<AgGridReact>(null);
    const {
        addComplaintAttachment,
        getSingleComplaintAttachment,
        deleteComplaintAttachment,
        getTheftMisappropriationDetail
    } = useTheftMisappropriationActions();
    const {dispatchGlobalData} = useContext(GlobalContext);
    const {watch, control, setValue, getValues, formState} = useFormContext();
    const editing = useRecoilValue(EDITING);
    const [attachmentRowId, setAttachmentRowId] = useState<number>();
    const [attachmentRowName, setAttachmentRowName] = useState<string>();
    const [openDeleteAttachmentModal, setOpenDeleteAttachmentModal] = useState(false);
    const setTheftMisappropriate = props.setTheftMisappropriate;
    const theftMisappropriate = props.theftMisappropriate;

    const columns: ColDef[] = [
        {
            field: "name",
            headerName: t('invoicingDetails.name'),
            unSortIcon: true,
            tooltipField: "name",
        },
        {
            field: "updateDatetime",
            headerName: t('invoicingDetails.updateDate'),
            cellRenderer: (params: any) => params.value ? dayjs(params.value).format("L") : "",
            unSortIcon: true,
        },
        {
            field: "_actions",
            headerName: t('eventCard.action'),
            pinned: 'right',
            lockPosition: 'right',
            lockPinned: true,
            sortable: false,
            width: 100,
            hide: !hasScope(THEFT_MISAPPROPRIATION_SCOPE_EDIT),
            cellRenderer: (params: ICellRendererParams) => {
                return (
                    <Box display="flex" justifyContent="center" alignItems="center">
                            <IconButton title="Details" color="accent"
                                     onClick={() => {
                                         getSingleComplaintAttachment(params.data.id, id).then((res) => {
                                             if (res) {
                                                 const byteCharacters = atob(res.result.file);
                                                 const byteNumbers = new Array(byteCharacters.length);
                                                 for (let i = 0; i < byteCharacters.length; i++) {
                                                     byteNumbers[i] = byteCharacters.charCodeAt(i);
                                                 }
                                                 const byteArray = new Uint8Array(byteNumbers);
                                                 const blob = new Blob([byteArray], { type: res.result.contentType });

                                                 const fileUrl = URL.createObjectURL(blob);

                                                 let fileWindow = window.open("");
                                                 if (fileWindow) {
                                                     fileWindow.document.write("<iframe width='100%' height='100%' src='" + fileUrl + "'></iframe>");
                                                 }

                                                 // @ts-ignore
                                                 fileWindow.onunload = () => {
                                                     URL.revokeObjectURL(fileUrl);
                                                 };
                                             }
                                         });
                                     }}>
                            <DescriptionRounded/>
                        </IconButton>
                            <IconButton
                            color={"accent"}
                            aria-label="delete"
                            onClick={(event) => {
                                setAttachmentRowId(params.data.id);
                                setAttachmentRowName(params.data.name);
                                setOpenDeleteAttachmentModal(true);
                            }}>
                            <DeleteRounded/>
                        </IconButton>
                    </Box>
                )
            }
        }
    ]

    const toolbar = {
        renderActions: () =>
            <>
                { hasScope(THEFT_MISAPPROPRIATION_SCOPE_EDIT) &&
                    <Button size="small" component="label">
                        <Add fontSize="small"/>{t('complainantDetails.addComplaint')}
                        <input onChange={(event) => {
                            setAttachment(event?.target?.files?.[0]);
                            const element = event.target as HTMLInputElement;
                            element.value = '';
                        }} hidden accept=".pdf, .png, .jpeg, .jpg" multiple type="file"/>
                    </Button>
                }
            </>,
        renderInfo: () =>
            <Typography align="left">{t('complainantDetails.complaint').toUpperCase()}</Typography>
    }

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data['id'],
        suppressRowClickSelection: true,
        domLayout: 'autoHeight',
        pagination: false,
        cacheBlockSize: 10,
        defaultColDef: {
            sortable: true,
            resizable: false,
            wrapHeaderText: true,
            autoHeight: true,
        },
        columnDefs: columns,
        serverSideInfiniteScroll: false,
        localeText: agGridLocale,
    };

    useEffect(() => {
        if (fileForImport) {
            let fileExtension = fileForImport.name.split('.').pop();
            let allowedExtensions = ["pdf", "png", "jpeg", "jpg"]
            if (allowedExtensions.includes(fileExtension!.toLowerCase())) {
                // Upload file
                addComplaintAttachment(fileForImport, id)
                    .then(response => {
                        if (response.status === "OK") {
                            dispatchGlobalData({
                                type: setSnackbar,
                                payload: {
                                    snackbar: {
                                        message: t("updateForm.successImport"),
                                        type: snackbarTypes.success,
                                        show: true,
                                        autoClose: true,
                                    }
                                }
                            });
                            getTheftMisappropriationDetail(id).then(res => {
                                setTheftMisappropriate(res);
                            });
                        } else {
                            dispatchGlobalData({
                                type: setSnackbar,
                                payload: {
                                    snackbar: {
                                        message: t(`handleErrors.${response.message}`),
                                        type: snackbarTypes.error,
                                        show: true,
                                        autoClose: true,
                                    }
                                }
                            });
                        }
                    })
                    .finally(() => {
                        setAttachment(undefined);
                    });
            } else {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message:t('handleErrors.wrongFileFormat'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
                setAttachment(undefined);
            }
        }
    }, [fileForImport])

    function checkDifferentComplaint() {
        if (!editing || (editing && !watch("differentComplaint"))) {
            return true;
        }
        if (editing && watch("differentComplaint")) {
            return false;
        }
        return true;
    }

    function checkComplaints(complaintToCheck: string) {
        if (!editing || (editing && !watch(complaintToCheck))) {
            return true;
        }
        if (editing && watch(complaintToCheck)) {
            return false;
        }
        return true;
    }


    return (
        <><Accordion defaultExpanded={true} onChange={(_e, expanded) => {
            setIsExpanded(expanded);
        }}>
            <AccordionSummary expandIcon={<ExpandMore/>}>
                <Grid item md={6} xs={6}>
                    {t("tabs.complainantDetails")}
                </Grid>
            </AccordionSummary>
            <AccordionDetails>
                <Grid container columnSpacing={2} rowSpacing={1}>
                    {!editing &&
                        <Grid item xs={12} sx={{padding: "16px"}}>
                            <AgGridCustomTable gridOptions={gridOptions} gridRef={gridRef} toolbar={toolbar}
                                               rowData={theftMisappropriate ? theftMisappropriate.complaintAttachments : []}/>
                        </Grid>}

                    <Grid container item xs={12} columnSpacing={3} rowSpacing={1}>
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name={"differentComplaint"}
                                render={({field, fieldState}) => (
                                    <FormControlLabel
                                        sx={{paddingTop: '15px'}}
                                        label={t("complainantDetails.differentComplaint")}
                                        control={
                                            <Checkbox color={"accent"}
                                                      disabled={!editing}
                                                      checked={field.value}
                                                      onChange={(e, checked) => {
                                                          if (!checked) {
                                                              setValue("complaintFirstName", "");
                                                              setValue("complaintLastName", "");
                                                          }
                                                          setValue("differentComplaint", checked);
                                                      }}
                                            />
                                        }
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name={"complaintFirstName"}
                                render={({field, fieldState}) => (
                                    <TextField
                                        fullWidth
                                        disabled={checkDifferentComplaint()}
                                        id="complaintFirstName"
                                        label={t("complainantDetails.firstName")}
                                        type="text"
                                        value={field.value}
                                        onChange={field.onChange}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name={"complaintLastName"}
                                render={({field, fieldState}) => (
                                    <TextField
                                        fullWidth
                                        disabled={checkDifferentComplaint()}
                                        id="complaintLastName"
                                        label={t("complainantDetails.lastName")}
                                        type="text" value={field.value}
                                        onChange={field.onChange}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                    <Grid container item xs={12} columnSpacing={3} rowSpacing={1}>
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name={"complaintDelivery"}
                                render={({field, fieldState}) => (
                                    <FormControlLabel
                                        sx={{paddingTop: '15px'}}
                                        label={t("complainantDetails.complaintDelivery")}
                                        control={
                                            <Checkbox color={"accent"}
                                                      disabled={!editing}
                                                      id="complaintDelivery"
                                                      checked={field.value}
                                                      onChange={(e, checked) => {
                                                          if (!checked) {
                                                              setValue("complaintDeliveryDatetime", null);
                                                          }
                                                          setValue("complaintDelivery", checked);
                                                      }}
                                            />
                                        }
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name={"complaintDeliveryDatetime"}
                                render={({field, fieldState}) => (
                                    <DatePicker
                                        label={t("complainantDetails.deliverydate")}
                                        value={field.value}
                                        onChange={field.onChange}
                                        disabled={checkComplaints("complaintDelivery")}
                                        renderInput={(params) =>
                                            <TextField
                                                {...params}
                                                fullWidth
                                                disabled={checkComplaints("complaintDelivery")}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                            />}
                                        inputFormat={"L"}
                                    />
                                )}/>
                        </Grid>
                    </Grid>
                    <Grid container item xs={12} columnSpacing={3} rowSpacing={1}>
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name={"keyDelivery"}
                                render={({field, fieldState}) => (
                                    <FormControlLabel
                                        sx={{paddingTop: '15px'}}
                                        label={t("complainantDetails.keyDelivery")}
                                        control={
                                            <Checkbox color={"accent"}
                                                      checked={field.value}
                                                      disabled={!editing}
                                                      id="keyDelivery"
                                                      onChange={(e, checked) => {
                                                          if (!checked) {
                                                              setValue("keyDeliveryDatetime", null);
                                                          }
                                                          setValue("keyDelivery", checked);
                                                      }}
                                            />
                                        }
                                    />
                                )}/>
                        </Grid>
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name={"keyDeliveryDatetime"}
                                render={({field, fieldState}) => (
                                    <DatePicker
                                        label={t("complainantDetails.deliverydate")}
                                        value={field.value}
                                        onChange={field.onChange}
                                        disabled={checkComplaints("keyDelivery")}
                                        renderInput={(params) =>
                                            <TextField
                                                {...params}
                                                fullWidth
                                                disabled={checkComplaints("keyDelivery")}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}/>}
                                        inputFormat={"L"}/>
                                )}/>
                        </Grid>
                    </Grid>
                    <Grid container item xs={12} columnSpacing={3} rowSpacing={1}>
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name={"complaintKeyDelivery"}
                                render={({field, fieldState}) => (
                                    <FormControlLabel
                                        sx={{paddingTop: '15px'}}
                                        label={t("complainantDetails.complaintKeyDelivery")}
                                        control={
                                            <Checkbox color={"accent"}
                                                      checked={field.value}
                                                      disabled={!editing}
                                                      id="complaintKeyDelivery"
                                                      onChange={(e, checked) => {
                                                          if (!checked) {
                                                              setValue("complaintKeyDeliveryDatetime", null);
                                                          }
                                                          setValue("complaintKeyDelivery", checked);
                                                      }}
                                            />
                                        }
                                    />
                                )}/>
                        </Grid>
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name={"complaintKeyDeliveryDatetime"}
                                render={({field, fieldState}) => (
                                    <DatePicker
                                        label={t("complainantDetails.deliverydate")}
                                        value={field.value}
                                        onChange={field.onChange}
                                        disabled={checkComplaints("complaintKeyDelivery")}
                                        renderInput={(params) =>
                                            <TextField
                                                {...params}
                                                fullWidth
                                                disabled={checkComplaints("complaintKeyDelivery")}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                            />}
                                        inputFormat={"L"}/>
                                )}/>
                        </Grid>
                    </Grid>
                    <Grid container item xs={12} columnSpacing={3} rowSpacing={1}>
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name={"lossOfPossession"}
                                render={({field, fieldState}) => (
                                    <FormControlLabel
                                        sx={{paddingTop: '15px'}}
                                        label={t("complainantDetails.lossOfPossession")}
                                        control={
                                            <Checkbox color={"accent"}
                                                      checked={field.value}
                                                      disabled={!editing}
                                                      id="lossOfPossession"
                                                      onChange={(e, checked) => {
                                                          if (!checked) {
                                                              setValue("lossOfPossessionDatetime", null);
                                                          }
                                                          setValue("lossOfPossession", checked);
                                                      }}
                                            />}
                                    />
                                )}/>
                        </Grid>
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name={"lossOfPossessionDatetime"}
                                rules={{ required: false }}                                                                
                                render={({field, fieldState}) => (
                                    <DatePicker
                                        label={t("complainantDetails.deliverydate")}
                                        value={field.value}
                                        onChange={field.onChange}
                                        disabled={checkComplaints("lossOfPossession")}                                                                                                                        
                                        renderInput={(params) => <TextField
                                            {...params}                                        
                                            fullWidth
                                            disabled={checkComplaints("lossOfPossession")}
                                            error={!!fieldState.error}
                                            required={false}
                                            helperText={fieldState.error?.message}/>}
                                        inputFormat={"L"}/>
                                )}/>
                        </Grid>
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
            <CustomModalBL title={t('updateForm.deleteConfirmation')}
                           content={t('updateForm.deleteConfirmationMsg', {fileName: attachmentRowName})}
                           cancelButton={t('buttons.cancel')}
                           saveButton={t('buttons.deleteDocument')}
                           open={openDeleteAttachmentModal}
                           titleIcon={<InfoRoundedIcon/>}
                           onClose={() => {
                               setOpenDeleteAttachmentModal(false);
                           }}
                           onSave={() => {
                               setOpenDeleteAttachmentModal(false);
                               deleteComplaintAttachment(attachmentRowId, id).then(res =>
                                   getTheftMisappropriationDetail(id).then(res => {
                                       setTheftMisappropriate(res);
                                   })
                               );
                           }}
            />
        </>
    );
}

export default ComplainantDetails;